// ActivarPage.js — Activación web de T&C (Axon Learning).
// Llega por enlace único (/activar/:token o /activar?t=) o QR genérico (/activar).
// Con token o número que matchea → muestra T&C + botón Aceptar. Al aceptar, el
// backend registra el consentimiento y dispara el contenido por WhatsApp.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

const BASE = `${API_BASE_URL}/activar.php`;
const LOGO = 'https://axiona.ateneomentoria.com/backend/uploads/wa-training/axon-blanco.png';
const TYC_PDF = 'https://axiona.ateneomentoria.com/backend/uploads/wa-training/Axon_Tratamiento_De_Datos.pdf';

const Wrap = styled.div`
  min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 1.3rem; background: linear-gradient(160deg, #0f766e 0%, #134e4a 100%);
  padding: 1.6rem 1.2rem; font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
`;
const Logo = styled.img`display: block; height: 44px; width: auto; object-fit: contain;`;
const Check = styled.label`
  display: flex; align-items: flex-start; gap: 0.55rem; text-align: left; font-size: 0.86rem;
  color: #334155; margin: 0.2rem 0 1rem; cursor: pointer; line-height: 1.45;
  input { margin-top: 0.15rem; width: 18px; height: 18px; flex: 0 0 auto; accent-color: #0f766e; }
  a { color: #0f766e; font-weight: 600; }
`;
const Card = styled.div`
  width: 100%; max-width: 460px; background: #fff; border-radius: 18px;
  box-shadow: 0 18px 50px rgba(0,0,0,0.25); padding: 2rem 1.6rem; text-align: center;
`;
const Marca = styled.div`font-weight: 800; color: #0f766e; letter-spacing: 0.5px; font-size: 1.1rem; margin-bottom: 1.2rem;`;
const Titulo = styled.h1`font-size: 1.35rem; color: #0f172a; margin: 0 0 0.4rem;`;
const Sub = styled.p`color: #475569; font-size: 0.95rem; line-height: 1.5; margin: 0.3rem 0 1.2rem;`;
const Tyc = styled.div`
  background: #f1f5f9; border-radius: 12px; padding: 1rem; text-align: left;
  color: #334155; font-size: 0.9rem; line-height: 1.55; margin: 0 0 0.8rem;
`;
const PdfLink = styled.a`display: inline-block; color: #0f766e; font-size: 0.85rem; margin-bottom: 1.2rem; font-weight: 600;`;
const Btn = styled.button`
  width: 100%; border: none; border-radius: 12px; padding: 0.95rem;
  background: #0f766e; color: #fff; font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: 0.2s; &:hover { background: #0d5f58; } &:disabled { opacity: 0.5; cursor: default; }
`;
const Input = styled.input`
  width: 100%; box-sizing: border-box; border: 1.5px solid #cbd5e1; border-radius: 12px;
  padding: 0.9rem 1rem; font-size: 1.05rem; margin-bottom: 0.9rem; text-align: center;
  &:focus { outline: none; border-color: #0f766e; }
`;
const Aviso = styled.p`color: #b91c1c; font-size: 0.85rem; margin: 0.2rem 0 0.8rem;`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const Spinner = styled.div`
  width: 42px; height: 42px; margin: 1.5rem auto; border: 4px solid #e2e8f0;
  border-top-color: #0f766e; border-radius: 50%; animation: ${spin} 0.8s linear infinite;
`;
const Emoji = styled.div`font-size: 3rem; margin-bottom: 0.6rem;`;

export default function ActivarPage() {
  const { token: tokenParam } = useParams();
  const [params] = useSearchParams();
  const urlParam = tokenParam || params.get('t') || '';
  const telParam = params.get('tel') || '';
  // Si lo que viene en la URL es un número (solo dígitos, 10-12), lo tratamos como
  // teléfono para identificar de una vez: /activar/573008676420 o /activar?tel=...
  const soloDigitos = (s) => /^\d{10,13}$/.test(s.replace(/\D/g, '')) && /^\d+$/.test(s);
  const telEnUrl = telParam || (soloDigitos(urlParam) ? urlParam : '');
  const token = telEnUrl ? '' : urlParam;

  const [vista, setVista] = useState('cargando'); // cargando|tyc|numero|exito|no_encontrado|ya_activo|error
  const [data, setData] = useState(null);         // {nombre, tyc_html, tyc_pdf, ref}
  const [telefono, setTelefono] = useState('');
  const [aceptaTyc, setAceptaTyc] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState('');

  // Busca por número. Devuelve la respuesta del backend (o null si error/invalido).
  const buscarTel = useCallback(async (rawTel) => {
    const tel = String(rawTel).replace(/\D/g, '');
    if (tel.length < 10) { setAviso('Escribe tu número de WhatsApp (10 dígitos).'); return null; }
    setAviso(''); setEnviando(true);
    try {
      const { data: d } = await axios.post(`${BASE}?action=buscar`, { telefono: tel });
      return d;
    } catch { setAviso('Hubo un problema. Intenta de nuevo.'); return null; }
    finally { setEnviando(false); }
  }, []);

  // Registra la aceptación con un ref dado (usado por el flujo de 1 paso y por el botón).
  const aceptarRef = useCallback(async (ref) => {
    if (!ref) return;
    setEnviando(true);
    try {
      const { data: d } = await axios.post(`${BASE}?action=aceptar`, { ref });
      if (d.ya_acepto) setVista('ya_activo');
      else if (d.ok) setVista('exito');
      else setVista('error');
    } catch { setVista('error'); }
    finally { setEnviando(false); }
  }, []);

  // Enruta según lo que devuelve buscar (para token / número en la URL).
  const handleBuscado = useCallback((d) => {
    if (!d) return;
    if (!d.found) setVista('no_encontrado');
    else if (d.ya_acepto) setVista('ya_activo');
    else { setData(d); setVista('tyc'); }
  }, []);

  // Entrada: número en la URL → identificar de una vez; token → resolver; nada → pedir número.
  useEffect(() => {
    let vivo = true;
    if (telEnUrl) { buscarTel(telEnUrl).then(d => { if (vivo) handleBuscado(d); }); return () => { vivo = false; }; }
    if (!token) { setVista('numero'); return; }
    axios.get(`${BASE}?t=${encodeURIComponent(token)}`)
      .then(({ data: d }) => {
        if (!vivo) return;
        if (d.needs_phone) { setVista('numero'); return; }
        if (d.ya_acepto) { setVista('ya_activo'); return; }
        setData(d); setVista('tyc');
      })
      .catch(() => vivo && setVista('numero'));
    return () => { vivo = false; };
  }, [token, telEnUrl, buscarTel, handleBuscado]);

  // Pantalla de número (manual): el check de T&C es el consentimiento, así que al
  // continuar se busca y, si existe, se activa en un solo paso (sin fricción).
  const continuarNumero = useCallback(async () => {
    if (!aceptaTyc) { setAviso('Debes aceptar los Términos y Condiciones para continuar.'); return; }
    const d = await buscarTel(telefono);
    if (!d) return;
    if (!d.found) { setVista('no_encontrado'); return; }
    if (d.ya_acepto) { setVista('ya_activo'); return; }
    await aceptarRef(d.ref);
  }, [aceptaTyc, telefono, buscarTel, aceptarRef]);

  // Botón "Acepto" de la pantalla tyc (flujo por token / número en URL).
  const aceptar = useCallback(() => aceptarRef(data?.ref), [data, aceptarRef]);

  const render = () => {
    switch (vista) {
      case 'cargando':
        return <><Spinner /><Sub>Un momento…</Sub></>;
      case 'tyc':
        return (
          <>
            <Titulo>Hola{data.nombre ? `, Dr(a). ${data.nombre}` : ''} 👋</Titulo>
            <Sub>Para activar tu acceso al programa, revisa y acepta:</Sub>
            <Tyc dangerouslySetInnerHTML={{ __html: data.tyc_html }} />
            {data.tyc_pdf && <PdfLink href={data.tyc_pdf} target="_blank" rel="noopener noreferrer">📄 Ver Términos y Condiciones completos</PdfLink>}
            <Btn onClick={aceptar} disabled={enviando}>{enviando ? 'Activando…' : 'Acepto y quiero recibir el contenido'}</Btn>
          </>
        );
      case 'numero':
        return (
          <>
            <Titulo>Doctor (a)</Titulo>
            <Sub>Digite su número de WhatsApp para empezar a recibir contenidos sobre diarrea o dolor.</Sub>
            <Input type="tel" inputMode="numeric" placeholder="Ej. 3001234567"
              value={telefono} onChange={e => setTelefono(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && continuarNumero()} />
            <Check>
              <input type="checkbox" checked={aceptaTyc} onChange={e => setAceptaTyc(e.target.checked)} />
              <span>Acepto los <a href={TYC_PDF} target="_blank" rel="noopener noreferrer">Términos y Condiciones</a> y autorizo el tratamiento de mis datos personales.</span>
            </Check>
            {aviso && <Aviso>{aviso}</Aviso>}
            <Btn onClick={continuarNumero} disabled={enviando || !aceptaTyc}>{enviando ? 'Activando…' : 'Continuar'}</Btn>
          </>
        );
      case 'exito':
        return (
          <>
            <Emoji>✅</Emoji>
            <Titulo>¡Listo!</Titulo>
            <Sub>En unos minutos recibirás tu primer contenido por WhatsApp. Revisa tu chat 📲</Sub>
          </>
        );
      case 'ya_activo':
        return (
          <>
            <Emoji>👍</Emoji>
            <Titulo>Ya estás activo</Titulo>
            <Sub>Tu acceso ya está habilitado. Sigue atento a tu WhatsApp para los contenidos.</Sub>
          </>
        );
      case 'no_encontrado':
        return (
          <>
            <Emoji>🔎</Emoji>
            <Titulo>No encontramos tu número</Titulo>
            <Sub>No está registrado en el programa. Comunícate con tu representante para que te incluya.</Sub>
            <Btn onClick={() => { setTelefono(''); setVista('numero'); }}>Intentar con otro número</Btn>
          </>
        );
      default:
        return (
          <>
            <Emoji>⚠️</Emoji>
            <Titulo>Algo salió mal</Titulo>
            <Sub>No pudimos completar la activación. Intenta de nuevo en un momento.</Sub>
            <Btn onClick={() => window.location.reload()}>Reintentar</Btn>
          </>
        );
    }
  };

  return (
    <Wrap>
      <Logo src={LOGO} alt="Axon Pharma" />
      <Card>
        <Marca>WhatsApp Learning</Marca>
        {render()}
      </Card>
    </Wrap>
  );
}
